import type { AppThemeConfig } from "@src/interfaces/theme-config.interface";
import { getThemeNameFromTag, OPTUM_THEME_NAME, THEME_CONFIGS, type ThemeName } from "@src/themes";
import type { FC, ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

export const THEME_STORAGE_KEY = "branding";

export const getStoredBranding = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return localStorage.getItem(THEME_STORAGE_KEY) ?? undefined;
};

export const syncStoredBrandingWithUtmTheme = (utmThemeTag?: string): ThemeName | undefined => {
  if (!utmThemeTag || typeof window === "undefined") {
    return undefined;
  }

  const storedBranding = getStoredBranding();
  if (!storedBranding) {
    return undefined;
  }

  const normalizedUtmTheme = getThemeNameFromTag(utmThemeTag);

  const normalizedStoredBranding = getThemeNameFromTag(storedBranding);
  if (normalizedStoredBranding === normalizedUtmTheme) {
    return normalizedStoredBranding;
  }

  localStorage.setItem(THEME_STORAGE_KEY, normalizedUtmTheme);
  window.dispatchEvent(
    new CustomEvent("brandingchange", {
      detail: { branding: normalizedUtmTheme },
    }),
  );

  return normalizedUtmTheme;
};

interface ThemeContextValue {
  activeThemeName: ThemeName;
  activeThemeConfig: AppThemeConfig;
  isDefaultTheme: boolean;
  setBranding: (branding?: string) => void;
  clearBranding: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeContextProvider: FC<{
  activeThemeName: ThemeName;
  children: ReactNode;
}> = ({ activeThemeName, children }) => {
  const [, setBrandingState] = useState<ThemeName>(() => {
    const storedBranding = getStoredBranding();
    return getThemeNameFromTag(storedBranding || activeThemeName);
  });

  const setBranding = useCallback((value?: string) => {
    const normalizedBranding = getThemeNameFromTag(value);

    if (typeof window !== "undefined") {
      localStorage.setItem(THEME_STORAGE_KEY, normalizedBranding);
      window.dispatchEvent(
        new CustomEvent("brandingchange", {
          detail: { branding: normalizedBranding },
        }),
      );
    }

    setBrandingState(normalizedBranding);
  }, []);

  const clearBranding = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(THEME_STORAGE_KEY, OPTUM_THEME_NAME);
      window.dispatchEvent(
        new CustomEvent("brandingchange", {
          detail: { branding: OPTUM_THEME_NAME },
        }),
      );
    }

    setBrandingState(OPTUM_THEME_NAME);
  }, []);

  const contextValue = useMemo(
    () => ({
      activeThemeName,
      activeThemeConfig: THEME_CONFIGS[activeThemeName],
      isDefaultTheme: activeThemeName === OPTUM_THEME_NAME,
      setBranding,
      clearBranding,
    }),
    [activeThemeName, clearBranding, setBranding],
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within ThemeContextProvider");
  }
  return context;
};
